import { AuthError } from '../../api/errors';
import { Sentry } from '../sentry';
import {
  clearStoredSession,
  getRefreshToken,
  storeOidcSession,
} from '../tokens';

import {
  exchangeOidcRefreshTokenForToken,
  isOidcGrantRejected,
  revokeOidcRefreshToken,
} from './oidc';

/**
 * Lifecycle of a session issued by the `/oidc` provider: serialised refresh
 * with rotation, and revocation on logout. Everything here works on the
 * *stored* session (AsyncStorage), which is what apiFetch reads, so both apps
 * share one implementation and one in-flight refresh.
 */

const listeners = new Set();

/**
 * Be told whenever a refresh produced a new access token, so the app can mirror
 * it into its store (redux keeps the decoded `exp`, which the web idle timer
 * reads). Returns an unsubscribe function.
 */
export const subscribeToOidcSessionRefresh = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const notifyRefreshed = (tokens) => {
  listeners.forEach((listener) => {
    try {
      listener(tokens);
    } catch (error) {
      Sentry.captureException(error);
    }
  });
};

const sessionExpiredError = (cause) => {
  const error = new AuthError('OIDC session has expired');
  error.code = cause?.code || 'invalid_grant';
  error.description = cause?.description;
  error.cause = cause;
  return error;
};

let inflightRefresh = null;

const performRefresh = async () => {
  const refreshToken = await getRefreshToken();

  if (!refreshToken) {
    await clearStoredSession();
    throw sessionExpiredError();
  }

  let tokens;
  try {
    tokens = await exchangeOidcRefreshTokenForToken({ token: refreshToken });
  } catch (error) {
    if (isOidcGrantRejected(error)) {
      // The refresh token is dead (expired, rotated away, or revoked). The
      // session is gone: drop it and never retry with the same token.
      await clearStoredSession();
      throw sessionExpiredError(error);
    }
    // Transient failure (network, 5xx): keep the stored tokens so the next
    // attempt can use them, and let the caller treat it as such.
    throw error;
  }

  // Persist the rotated refresh token before anyone can use the new access
  // token — the old refresh token is dead from this point on.
  await storeOidcSession(tokens);
  notifyRefreshed(tokens);

  return tokens;
};

/**
 * Refresh the stored `/oidc` session. Concurrent callers (parallel 401s, a
 * near-expiry check racing a retry) all await the same request, so exactly one
 * refresh is ever in flight — a second refresh with the same rotating token
 * would revoke the entire login.
 *
 * Resolves with the new tokens after they have been stored. Rejects with an
 * AuthError (status 401, `code: 'invalid_grant'`) when the session is gone —
 * the stored tokens are cleared by then — or with the underlying error when
 * the failure was transient.
 */
export const refreshOidcSession = () => {
  if (!inflightRefresh) {
    inflightRefresh = performRefresh().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
};

/**
 * End the stored `/oidc` session: discard it locally, then revoke the refresh
 * token on the server (which also invalidates the access token). Local cleanup
 * comes first so a new login that starts while the revocation request is in
 * flight (a biometric re-auth right after the app discards a session) can
 * never be wiped by it. Revocation is best effort; a failure is recorded by
 * the client and does not throw.
 */
export const revokeOidcSession = async () => {
  const refreshToken = await getRefreshToken();
  await clearStoredSession();
  return revokeOidcRefreshToken(refreshToken);
};

// Test hook
export const resetOidcSessionState = () => {
  inflightRefresh = null;
  listeners.clear();
};
